import { useCallback, useRef, useState } from "react";
import api from "../../api/client";
import { geocodeSubscriberAddress } from "../../services/geocodingService";

/**
 * One-time in-app backfill: geocodes every For Dispatch record that has no
 * precise saved location and persists its lat/lon.
 *
 * The geocoding runs IN THE BROWSER using the exact same hierarchical lookup
 * (`geocodeSubscriberAddress`) that fires when a user clicks a subscriber's
 * name on the dispatch map. This is required because the app's Google key is
 * referer-restricted (browser-only) — a server-side script cannot use it.
 *
 * Coordinates are saved one record at a time via the existing
 * `PUT /migration/dispatch-dashboard/{id}/coordinates` endpoint, which also
 * flips `location_saved = true` so the map flies straight to the precise pin.
 */

const CONCURRENCY = 5;
// "Precise enough" accuracy buckets returned by geocodeSubscriberAddress.
const PRECISE_ACCURACY = new Set(["exact", "address", "street"]);

const EMPTY_STATS = { saved: 0, precise: 0, approx: 0, failed: 0, errors: 0 };

export default function CoordinateBackfillTool({ onComplete }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | loading | ready | running | done | error
  const [pending, setPending] = useState([]);
  const [processed, setProcessed] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [errorMsg, setErrorMsg] = useState("");
  // When ON, barangay/municipality-level results are NOT saved (left for a
  // future precise lookup or manual pin). Default OFF → save every result so
  // every record ends up with a coordinate.
  const [preciseOnly, setPreciseOnly] = useState(false);

  const stopRef = useRef(false);

  const openTool = useCallback(async () => {
    setOpen(true);
    setPhase("loading");
    setErrorMsg("");
    setProcessed(0);
    setStats(EMPTY_STATS);
    stopRef.current = false;
    try {
      const { data } = await api.get("/migration/dispatch-dashboard/backfill-pending");
      setPending(data);
      setPhase("ready");
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Failed to load pending records.");
      setPhase("error");
    }
  }, []);

  const close = useCallback(() => {
    stopRef.current = true;
    setOpen(false);
    setPhase("idle");
    if (onComplete) onComplete();
  }, [onComplete]);

  const processOne = useCallback(async (rec, preciseOnlyFlag) => {
    let fullAddress = rec.address || "";
    if (rec.village && !fullAddress.toUpperCase().includes(rec.village.toUpperCase())) {
      fullAddress = `${fullAddress} ${rec.village}`.trim();
    }

    let result;
    try {
      result = await geocodeSubscriberAddress({
        address: fullAddress,
        barangay: rec.barangay,
        municipality: rec.municipality,
        latitude: null,
        longitude: null,
      });
    } catch {
      return "failed";
    }

    if (!result?.success || !result.latitude || !result.longitude) return "failed";

    const isPrecise = PRECISE_ACCURACY.has(result.accuracy);
    if (preciseOnlyFlag && !isPrecise) return "skipped_approx";

    try {
      await api.put(`/migration/dispatch-dashboard/${rec.id}/coordinates`, {
        latitude: result.latitude,
        longitude: result.longitude,
      });
    } catch {
      return "save_error";
    }
    return isPrecise ? "precise" : "approx";
  }, []);

  const start = useCallback(async () => {
    setPhase("running");
    stopRef.current = false;
    const items = pending;
    const preciseOnlyFlag = preciseOnly;
    let index = 0;

    const bump = (outcome) => {
      setProcessed((p) => p + 1);
      setStats((s) => {
        const n = { ...s };
        if (outcome === "precise") { n.saved += 1; n.precise += 1; }
        else if (outcome === "approx") { n.saved += 1; n.approx += 1; }
        else if (outcome === "failed" || outcome === "skipped_approx") { n.failed += 1; }
        else if (outcome === "save_error") { n.errors += 1; }
        return n;
      });
    };

    const worker = async () => {
      while (index < items.length && !stopRef.current) {
        const i = index++;
        const outcome = await processOne(items[i], preciseOnlyFlag);
        bump(outcome);
      }
    };

    const runners = [];
    for (let c = 0; c < CONCURRENCY; c++) runners.push(worker());
    await Promise.all(runners);

    setPhase("done");
  }, [pending, preciseOnly, processOne]);

  const stop = useCallback(() => { stopRef.current = true; }, []);

  const total = pending.length;
  const pct = total ? Math.round((processed / total) * 100) : 0;

  return (
    <>
      <button
        type="button"
        onClick={openTool}
        title="Geocode all For Dispatch records that have no saved location"
        className="inline-flex items-center gap-1.5 rounded border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 whitespace-nowrap"
      >
        📍 Backfill Coordinates
      </button>

      {open && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold mb-1">Backfill For Dispatch Coordinates</h3>
            <p className="text-xs text-gray-500 mb-4">
              Geocodes each address in your browser (same lookup as clicking a subscriber name)
              and saves a precise pin. Keep this tab open and focused while it runs.
            </p>

            {phase === "loading" && (
              <p className="text-sm text-gray-600 py-6 text-center">Loading pending records…</p>
            )}

            {phase === "error" && (
              <p className="text-sm text-red-600 py-4">{errorMsg}</p>
            )}

            {phase === "ready" && (
              <div className="space-y-4">
                <div className="rounded-lg bg-gray-50 border px-4 py-3 text-sm">
                  <span className="font-semibold text-gray-900">{total.toLocaleString()}</span>{" "}
                  record{total === 1 ? "" : "s"} without a saved location.
                  {total === 0 && <p className="text-green-600 mt-1">Nothing to backfill — all records already have coordinates. 🎉</p>}
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={preciseOnly} onChange={(e) => setPreciseOnly(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <span>Only save street-level or better. Records that resolve only to barangay/municipality center are skipped (left without a pin).</span>
                </label>
              </div>
            )}

            {(phase === "running" || phase === "done") && (
              <div className="space-y-3">
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div className="bg-indigo-600 h-2.5 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-sm text-gray-700 text-center">
                  {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded bg-green-50 border border-green-200 px-3 py-2">
                    <div className="font-semibold text-green-700">{stats.precise.toLocaleString()}</div>
                    <div className="text-green-600">precise (street+)</div>
                  </div>
                  <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2">
                    <div className="font-semibold text-amber-700">{stats.approx.toLocaleString()}</div>
                    <div className="text-amber-600">barangay-level</div>
                  </div>
                  <div className="rounded bg-gray-50 border px-3 py-2">
                    <div className="font-semibold text-gray-700">{stats.failed.toLocaleString()}</div>
                    <div className="text-gray-500">not located</div>
                  </div>
                  <div className="rounded bg-red-50 border border-red-200 px-3 py-2">
                    <div className="font-semibold text-red-700">{stats.errors.toLocaleString()}</div>
                    <div className="text-red-600">save errors</div>
                  </div>
                </div>
                {phase === "done" && (
                  <p className="text-sm font-medium text-green-700 text-center pt-1">
                    Done — {stats.saved.toLocaleString()} record{stats.saved === 1 ? "" : "s"} saved.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              {phase === "ready" && total > 0 && (
                <button type="button" onClick={start} className="px-4 py-2 text-sm text-white rounded bg-indigo-600 hover:bg-indigo-700">Start</button>
              )}
              {phase === "running" && (
                <button type="button" onClick={stop} className="px-4 py-2 text-sm rounded border border-red-300 text-red-600 hover:bg-red-50">Stop</button>
              )}
              <button
                type="button"
                onClick={close}
                disabled={phase === "running"}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                {phase === "done" ? "Close" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
